import React, { useContext, useEffect, useState } from 'react';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { InputText } from 'primereact/inputtext';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import 'primeflex/primeflex.css';
import { ToastContext } from 'app/context/ToastContext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import * as Yup from 'yup';
import { createBonus, getBonusDeductionById, updateBonus } from 'app/api/bonusDeductionApi';
import AgentWidget from 'app/component/agent-widget';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox'; // Make sure PrimeFlex is imported

const validationSchema = Yup.object({
  agent: Yup.object().required('Poradca je povinný'),
});

const CreateBonusDeduction: React.FC = () => {
  const { id } = useParams<{ id?: string }>(); // Capture id from URL

  const [searchParamsInitial] = useSearchParams();
  const tempParameters = searchParamsInitial.get('p');

  const navigate = useNavigate();
  const { showSuccess, showError } = useContext(ToastContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (id) {
      getBonusDeductionById(id)
        .then(response => {
          if (response.data) {
            setData(response.data);
          }
        })
        .catch(error => {
          console.error(error);
          showError('Chyba', error.response?.data?.detail);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Handle form submission
  const handleSubmit = (values: any) => {
    setIsSaving(true);

    let call;
    if (id) {
      call = updateBonus(id, values);
    } else {
      call = createBonus(values);
    }

    call
      .then(response => {
        showSuccess('Úspech', 'Bonus/zrážka úspešne uložený.');
        setData(response.data);
        navigate(`/bonus-deductions?p=${tempParameters}`);
      })
      .catch(error => {
        showError('Chyba', error.response?.data?.detail);
        console.error('Error creating agent:', error);
      })
      .finally(() => setIsSaving(false));
  };

  const onCancel = () => {
    navigate(`/bonus-deductions?p=${tempParameters}`);
  };

  return (
    <div className="card">
      {loading && (
        <div className="flex justify-content-center">
          <ProgressSpinner />
        </div>
      )}

      {!loading && (
        <>
          <h2>{id ? 'Upraviť dáta bonusu/zrážky' : 'Vytvoriť nový bonus alebo zrážku'}</h2>
          <Formik
            initialValues={
              data
                ? data
                : {
                    data: null,
                    documentName: '',
                  }
            }
            validationSchema={validationSchema}
            onSubmit={values => {
              handleSubmit(values);
            }}
          >
            {({ isSubmitting, setFieldValue, values, errors }) => {
              return (
                <Form onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} className="p-fluid">
                  <div className="grid">
                    <div className="col-12">
                      <div className="p-field">
                        <label htmlFor="description">Opis</label>
                        <Field name="description" as={InputText} />
                        <ErrorMessage name="description" component="div" className="p-error" />
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="p-field">
                        <label htmlFor="agent">Poradca</label>
                        <AgentWidget
                          id="agent"
                          value={values.agent?.id}
                          onPersonChanged={e => {
                            void setFieldValue('agent.id', e);
                          }}
                          showCareer={false}
                          onCareerChanged={e => {}}
                        />
                        <ErrorMessage name="agent" component="div" className="p-error" />
                      </div>
                    </div>

                    <div className="col-12 md:col-6">
                      <div className="p-field">
                        <label htmlFor="value">Hodnota v eurách (ak je zrážka, tak záporné číslo)</label>
                        <InputNumber
                          id="value"
                          value={values.value}
                          locale="sk"
                          minFractionDigits={0}
                          maxFractionDigits={2}
                          onValueChange={e => void setFieldValue('value', e.value)}
                        />
                      </div>
                    </div>

                    <div className="col-12 md:col-6">
                      <div className="p-field">
                        <label htmlFor="applied">Už aplikované: </label>
                        <Checkbox
                          name="applied"
                          disabled={true}
                          onChange={e => void setFieldValue('applied', e.checked)}
                          checked={values.applied}
                        ></Checkbox>
                        <ErrorMessage name="applied" component="div" className="p-error" />
                      </div>
                    </div>
                    <div className="col-12 md:col-6">
                      <div className="p-field">
                        <label htmlFor="forStornofond">Do stornofondu: </label>
                        <Checkbox
                          name="forStornofond"
                          onChange={e => void setFieldValue('forStornofond', e.checked)}
                          checked={values.forStornofond}
                        ></Checkbox>
                        <ErrorMessage name="forStornofond" component="div" className="p-error" />
                      </div>
                    </div>
                    <div className="col-12 md:col-6">
                      <div className="p-field">
                        <label htmlFor="forProvisions">Do provízie: </label>
                        <Checkbox
                          name="forProvisions"
                          onChange={e => void setFieldValue('forProvisions', e.checked)}
                          checked={values.forProvisions}
                        ></Checkbox>
                        <ErrorMessage name="forProvisions" component="div" className="p-error" />
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '10px' }}>
                    <Button
                      type="button"
                      label="Späť"
                      style={{ backgroundColor: 'gray' }}
                      icon="pi pi-arrow-left"
                      className="p-button-secondary"
                      onClick={() => navigate(`/bonus-deductions?p=${tempParameters}`)}
                    />
                    <Button style={{ backgroundColor: '#e31818' }} type="submit" label="Uložiť" icon="pi pi-check" disabled={isSaving} />
                  </div>
                </Form>
              );
            }}
          </Formik>
        </>
      )}
    </div>
  );
};

export default CreateBonusDeduction;
